import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Typography,
    Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import ScreenBanner from "../../../../../components/ScreenBanner";
import PermissionGuard, { usePermission } from "../../../../../components/PermissionGuard";
import moment from "moment";
import useDebounce from "../../../../../lib/useDebounce";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from '@mui/icons-material/Add';
import { IDataFluxoCaixaSimulacao } from "../../../../../types/api/financeiro/fluxo-caixa-simulacoes";
import DialogAddEditSimulacao from "../DialogAddEditSimulacao";
import SkeletonListarSimulacoes from "./SkeletonListarSimulacoes";

function ListagemSimulacoesContent() {

    const [palavraChave, setPalavraChave] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openModal, setOpenModal] = useState<TOpenModalSimulacoes>(null)

    const permiteAdicionar = usePermission('financeiro.fluxo-caixa-gerar-simulacao')


    // const { openModal } = useNotaFiscal();

    const debouncedSearchTerm = useDebounce(palavraChave);

    const { data, isFetching } = useQuery(
        ["pesquisar-simulacoes-fluxo-caixa", debouncedSearchTerm, page, rowsPerPage],
        async () => {
            const params = {
                pesquisar: debouncedSearchTerm,
                page: page + 1,
                limit: rowsPerPage,
            };
            const res = await axiosClient.get("/financeiro/fluxo-caixa/pesquisar-simulacoes", { params });
            return res.data;
        },
        { keepPreviousData: true }
    );

    const { requestSimulacao, dataSimulacao } = useMemo(
        () => ({
            requestSimulacao: data,
            dataSimulacao: data?.data,
        }),
        [data]
    );

    useEffect(() => {
        const handleEnter = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                setPage(0);
            }
        };
        window.addEventListener("keydown", handleEnter);
        return () => window.removeEventListener("keydown", handleEnter);
    }, []);

    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleModalAction = useCallback((action: "open" | "close", id?: true | number, value?: IDataFluxoCaixaSimulacao | null) => {
        if (action === "close") setOpenModal(null)
        if (action === "open" && id && value) setOpenModal({ open: id, defaultValue: value })
        if (action === "open" && id && !value) setOpenModal({ open: id, defaultValue: null })
    }, [])

    return (
        <Box sx={{ background: "#F5F6FA", minHeight: "100vh" }}>
            <Container maxWidth="xl" sx={{ mt: 5 }}>
                <Typography textAlign="center" variant="h5" mb={3}>
                    Listagem de simulações
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent={'space-between'} gap={2} py={2} mb={2}>
                                <TextField
                                    value={palavraChave}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setPalavraChave(palavraChave)}>
                                                        <SearchIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setPalavraChave(palavraChave);
                                        }
                                    }}
                                    onChange={(e) => setPalavraChave(e.target.value)}
                                />

                                {
                                    permiteAdicionar &&
                                    <Button
                                        sx={{ height: '56px' }}
                                        variant="outlined"
                                        startIcon={<AddIcon sx={{ ml: 1 }} />}
                                        onClick={() => { handleModalAction("open", true, null) }}
                                    >
                                        Nova simulação
                                    </Button>
                                }
                            </Box>

                            {
                                isFetching && (
                                    <SkeletonListarSimulacoes />
                                )
                            }

                            {
                                !isFetching && dataSimulacao && (
                                    <>
                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><b>ID/Código</b></TableCell>
                                                        <TableCell><b>Responsável</b></TableCell>
                                                        <TableCell><b>Descrição</b></TableCell>
                                                        <TableCell><b>Período de análise/Última atualização</b></TableCell>
                                                        <TableCell><b>Status</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dataSimulacao.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={5}>
                                                                <DisplayNotFoundInfo
                                                                    label="Nenhuma simulação encontrada" />
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        dataSimulacao.map((row: IDataFluxoCaixaSimulacao) => (
                                                            <TableRow key={row.id} hover>
                                                                <TableCell>
                                                                    <Button
                                                                        variant="text"
                                                                        sx={{
                                                                            color: "primary.main",
                                                                            textDecoration: "underline",
                                                                            fontWeight: "bold",
                                                                            fontSize: "1rem",
                                                                            padding: 0,
                                                                            minWidth: "unset"
                                                                        }}
                                                                        onClick={() => {

                                                                            // openModal(row.id.toString());
                                                                        }}
                                                                    >
                                                                        {row.id || 'N/A'}
                                                                    </Button>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5 }}
                                                                    >
                                                                        {row.codigo || 'N/A'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack gap={0.5}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {row.criado_por || 'N/A'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack gap={0.5}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {row.descricao || 'N/A'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="info"
                                                                        sx={{ fontSize: "0.85rem", mt: 0.5 }}
                                                                    >
                                                                        {`Vigência: ${moment(row.perido_analise_inicio).format("DD/MM/YYYY")} - ${moment(row.perido_analise_fim).format("DD/MM/YYYY")}`}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5 }}
                                                                    >
                                                                        {`Atualizado em: ${moment(row.atualizado_em).format("DD/MM/YYYY")}`}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        size="small"
                                                                        label={row.ativo ? "Ativo" : "Inativo"}
                                                                        sx={{
                                                                            backgroundColor: row.ativo ? "success.main" : "error.main",
                                                                            color: "#fff",
                                                                        }}
                                                                        color="default"
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                            <Stack direction="row" justifyContent="flex-end">
                                                <TablePagination
                                                    component="div"
                                                    count={requestSimulacao?.total}
                                                    page={page}
                                                    onPageChange={handleChangePage}
                                                    rowsPerPage={rowsPerPage}
                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                    labelRowsPerPage="Linhas por página"
                                                />
                                            </Stack>
                                        </Box>
                                    </>
                                )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
            {
                Boolean(openModal?.open) &&
                <DialogAddEditSimulacao
                    defaultValue={openModal?.defaultValue ? openModal : null}
                    open={Boolean(openModal?.open)}
                    handleClose={() => handleModalAction("close")}
                />
            }
        </Box>
    );
}

export default function ListarNotasFiscais() {
    return (
        <>
            <ScreenBanner color="#1E2E6B" title="Financeiro" />
            <PermissionGuard permissionId="financeiro.fluxo-caixa-listar-simulacoes">
                <ListagemSimulacoesContent />
            </PermissionGuard>
        </>
    );
}

export type TOpenModalSimulacoes = {
    open: number | boolean | null;
    defaultValue: IDataFluxoCaixaSimulacao | null;
} | null